import React, { useCallback, useMemo, useState } from "react";
import { Input, InputProps, AHText, Box } from "arkh";
import {
  AsYouType,
  isValidPhoneNumber,
  parsePhoneNumberWithError,
  CountryCode,
} from "libphonenumber-js";
import { FormattedMessage } from "react-intl";

type PhoneInputProps = Omit<InputProps, "value" | "onChangeText"> & {
  value: string;
  onChangeText: (value: string) => void;
  defaultCountry?: CountryCode;
  showValidation?: boolean;
};

export function PhoneInput({
  value,
  onChangeText,
  defaultCountry = "SE",
  showValidation = true,
  ...props
}: PhoneInputProps) {
  const [isFocused, setIsFocused] = useState(false);
  const formatter = useMemo(() => {
    return new AsYouType(defaultCountry);
  }, [defaultCountry]);

  const formattedValue = useMemo(() => {
    if (!value) return "";

    formatter.reset();
    return formatter.input(value);
  }, [value, formatter]);

  const isValid = useMemo(() => {
    if (!value || value.length < 4) return null;
    return isValidPhoneNumber(value, defaultCountry);
  }, [value, defaultCountry]);

  const handleChangeText = useCallback(
    (text: string) => {
      // Allow digits, +, spaces, and dashes for user input
      const cleaned = text.replace(/[^\d+\s\-()]/g, "");
      onChangeText(cleaned);
    },
    [onChangeText]
  );

  const handleBlur = useCallback(
    (e: any) => {
      setIsFocused(false);
      // On blur, try to format to E.164 if valid
      if (value && isValidPhoneNumber(value, defaultCountry)) {
        try {
          const parsed = parsePhoneNumberWithError(value, defaultCountry);
          onChangeText(parsed.formatInternational());
        } catch {
          // Keep original value if parsing fails
        }
      }
      props.onBlur?.(e);
    },
    [value, defaultCountry, props, onChangeText]
  );

  const handleFocus = useCallback(
    (e: any) => {
      setIsFocused(true);
      props.onFocus?.(e);
    },
    [props]
  );

  const showError = showValidation && !isFocused && isValid === false;

  return (
    <Box gap="small">
      <Input
        {...props}
        value={formattedValue}
        onChangeText={handleChangeText}
        onFocus={handleFocus}
        onBlur={handleBlur}
        keyboardType="phone-pad"
        textContentType="telephoneNumber"
        returnKeyType="none"
        autoComplete="tel"
        borderColor={showError ? "negative" : props.borderColor}
      />
      {showError && (
        <AHText type="detail" color="negative">
          <FormattedMessage
            defaultMessage="Invalid phone number"
            description="PhoneInput: Invalid Phone Number"
          />
        </AHText>
      )}
    </Box>
  );
}
